import { useEffect } from "react";
import { Tabs, Dropdown, Empty } from "antd";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTabsStore, TabItem } from "./tabsStore";
import { routeObjects, deriveTabTitle } from "./tabRoutes";
import { TabPaneProvider } from "./tabContext";

/**
 * A single keep-alive pane. `useRoutes(routeObjects, tab.path)` resolves the
 * page for this tab's own URL, so its params/location are correct even while
 * it's in the background. antd keeps rendered panes mounted, so state (filters,
 * scroll, form input) survives tab switches.
 *
 * `tab.path` must be the **whole** URL, query string included. Passing a
 * location to `useRoutes` scopes `useLocation` — and therefore
 * `useSearchParams` — to that string for everything below, so a pathname-only
 * `tab.path` makes every page in the workspace read an empty `?…`. Writing one
 * still worked (`useNavigate` goes to the real router), which is what made this
 * so quiet: the URL changed, the page never saw it, and the control that set it
 * looked dead. Template setup's Details/Sections/Documents steps were exactly
 * this bug.
 */
const TabPane = ({ tab }: { tab: TabItem }) => {
  const active = useTabsStore((s) => s.activeKey) === tab.key;
  const element = useRoutes(routeObjects, tab.path);
  return (
    <TabPaneProvider tabKey={tab.key} active={active}>
      {element}
    </TabPaneProvider>
  );
};

const TabbedWorkspace = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = useTabsStore((s) => s.tabs);
  const activeKey = useTabsStore((s) => s.activeKey);
  const openTab = useTabsStore((s) => s.openTab);
  const removeTab = useTabsStore((s) => s.removeTab);
  const closeOthers = useTabsStore((s) => s.closeOthers);
  const closeToRight = useTabsStore((s) => s.closeToRight);
  const closeAll = useTabsStore((s) => s.closeAll);

  // The browser URL is the source of truth for the active tab. Any navigation
  // (menu click, link, back/forward, deep link) opens or focuses a tab.
  useEffect(() => {
    openTab(
      location.pathname + location.search,
      deriveTabTitle(location.pathname),
    );
  }, [location.pathname, location.search, openTab]);

  const focusTab = (key: string) => {
    const tab = tabs.find((tb) => tb.key === key);
    if (tab) navigate(tab.path);
  };

  const closeTab = (key: string) => {
    const idx = tabs.findIndex((tb) => tb.key === key);
    const wasActive = key === activeKey;
    const rest = tabs.filter((tb) => tb.key !== key);
    removeTab(key);
    if (wasActive) {
      const next = rest[idx] ?? rest[idx - 1] ?? rest[rest.length - 1];
      navigate(next ? next.path : "/");
    }
  };

  const runContextAction = (action: string, key: string) => {
    switch (action) {
      case "close":
        closeTab(key);
        break;
      case "others":
        closeOthers(key);
        navigate(tabs.find((tb) => tb.key === key)?.path ?? "/");
        break;
      case "right":
        closeToRight(key);
        break;
      case "all":
        closeAll();
        navigate("/");
        break;
    }
  };

  const items = tabs.map((tab) => ({
    key: tab.key,
    // Non-closable if it's the only tab, so the workspace is never empty.
    closable: tabs.length > 1,
    label: (
      <Dropdown
        trigger={["contextMenu"]}
        menu={{
          items: [
            { key: "close", label: t("Close") },
            { key: "others", label: t("Close others") },
            { key: "right", label: t("Close to the right") },
            { key: "all", label: t("Close all") },
          ],
          onClick: ({ key: action }) => runContextAction(action, tab.key),
        }}
      >
        <span>{tab.title}</span>
      </Dropdown>
    ),
    children: <TabPane tab={tab} />,
  }));

  if (tabs.length === 0) {
    return <Empty description={t("No open tabs")} style={{ marginTop: 48 }} />;
  }

  return (
    <Tabs
      type="editable-card"
      hideAdd
      activeKey={activeKey}
      onChange={focusTab}
      onEdit={(targetKey, action) => {
        if (action === "remove") closeTab(targetKey as string);
      }}
      items={items}
    />
  );
};

export default TabbedWorkspace;
